import { useEffect, useRef, useState } from 'react';
import { preloadImage } from './imagePreload';

function normalizeSrc(src: string | null | undefined): string | null {
    if (typeof src !== 'string') return null;
    const trimmed = src.trim();
    return trimmed || null;
}

/**
 * Keeps the previous image visible while preloading a new src, then swaps atomically.
 * Returns null for displaySrc on first load until the image is ready (caller shows initials).
 */
export function useSeamlessImageSrc(targetSrc: string | null | undefined): {
    displaySrc: string | null;
    isLoading: boolean;
} {
    const normalizedTarget = normalizeSrc(targetSrc);
    const [displaySrc, setDisplaySrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const displaySrcRef = useRef<string | null>(null);

    useEffect(() => {
        displaySrcRef.current = displaySrc;
    }, [displaySrc]);

    useEffect(() => {
        if (!normalizedTarget) {
            setDisplaySrc(null);
            setIsLoading(false);
            return;
        }

        if (normalizedTarget === displaySrcRef.current) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        void preloadImage(normalizedTarget).then((ok) => {
            if (cancelled) return;
            if (ok) {
                setDisplaySrc(normalizedTarget);
            }
            setIsLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [normalizedTarget]);

    return { displaySrc, isLoading };
}
